import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import SubscriptionFormDirect from "./SubscriptionFormDirect";

// Get Stripe publishable key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
const planId = process.env.REACT_APP_STRIPE_PRICE_ID || "";
const planPrice = process.env.REACT_APP_STRIPE_PRICE_AMOUNT ? Number(process.env.REACT_APP_STRIPE_PRICE_AMOUNT) : 0;

interface SubscriptionLimitModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SubscriptionLimitModal: React.FC<SubscriptionLimitModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<"info" | "form">("info");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleProceedToSubscription = () => {
    setStep("form");
  };

  const handleSubscriptionSuccess = () => {
    setLoading(false);
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const handleBack = () => {
    setStep("info");
  };

  const handleGoToPricing = () => {
    onClose();
    navigate("/pricing");
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: "#1a1f2e",
          color: "#ffffff"
        }
      }}
    >
      {step === "info" ? (
        <>
          <DialogTitle sx={{ 
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            padding: "20px 24px",
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: "600", 
                fontSize: "1.5rem",  // Equivalente a un h5 pero sin usar la variante h5
                margin: 0
              }}
            >
              Transcription Limit Reached
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <Typography paragraph>
              You've reached the limit of {process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5'} transcriptions on your Personal plan.
            </Typography>
            <Typography paragraph>
              Upgrade to our Pro plan for just $9.99/month to unlock unlimited transcriptions and continue enjoying all the features of Tranzcribe.
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: "500" }}>
                Pro Plan Benefits:
              </Typography>
            </Box>
            <Box sx={{ px: 2 }}>
              <Typography variant="body1" component="div" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                • Unlimited transcriptions
              </Typography>
              <Typography variant="body1" component="div" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                • All features from the Personal plan
              </Typography>
              <Typography variant="body1" component="div" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                • Priority support
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            padding: "16px 24px", 
            borderTop: "1px solid rgba(255,255,255,0.1)",
            justifyContent: "space-between" 
          }}>
            <Button 
              onClick={onClose}
              sx={{ color: "rgba(255,255,255,0.7)" }}
            >
              Not Now
            </Button>
            <Box>
              <Button 
                onClick={handleGoToPricing}
                sx={{ color: "#3b82f6", mr: 1 }}
              >
                View Plans
              </Button>
              <Button 
                variant="contained" 
                onClick={handleProceedToSubscription}
                disabled={loading}
                sx={{ backgroundColor: "#3b82f6" }}
              >
                {loading ? <CircularProgress size={24} /> : "Upgrade to Pro"}
              </Button>
            </Box>
          </DialogActions>
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Elements stripe={stripePromise}>
            <SubscriptionFormDirect 
              planId={planId}
              planPrice={planPrice}
              onSuccess={handleSubscriptionSuccess}
              onCancel={handleBack}
            />
          </Elements>
        </Box>
      )}
    </Dialog>
  );
}; 